/**
 * Échanges d'un cabinet — e-mails et WhatsApp dans UN seul fil (bible §9.3).
 *
 * Pourquoi : jusqu'ici, savoir ce qu'on s'était dit avec un cabinet demandait
 * d'ouvrir le module E-mails, puis le module Messages, puis de recouper les
 * dates de tête. L'onglet de la fiche annonçait « les e-mails arrivent ici une
 * fois ta boîte connectée » alors que les boîtes étaient connectées depuis des
 * jours : une promesse qui ne se réalisait jamais.
 *
 * CONFIDENTIALITÉ (§11.2) : le corps d'un e-mail n'est jamais stocké ni servi.
 * On expose l'objet, l'interlocuteur et la date — de quoi retrouver l'échange,
 * pas de quoi le relire ici.
 */
import { z } from "zod";
import { EchangeResumeSchema } from "./resumes";

export const CANAUX = ["email", "whatsapp"] as const;
export type Canal = (typeof CANAUX)[number];

export const EchangeSchema = z.object({
  id: z.string(),
  canal: z.enum(CANAUX),
  /** horodatage ISO de l'échange lui-même, jamais de son enregistrement */
  quand: z.string(),
  direction: z.enum(["entrant", "sortant"]),
  /** objet de l'e-mail ; null pour WhatsApp, qui n'en a pas */
  sujet: z.string().nullable(),
  /** aperçu court du dernier message WhatsApp ; null pour un e-mail (corps non stocké) */
  apercu: z.string().nullable(),
  /** qui est en face : adresse e-mail, ou numéro/nom de la conversation */
  interlocuteur: z.string(),
  /** où ouvrir l'échange complet */
  lien: z.string(),
  /**
   * Sur le fil d'un PRATICIEN : cet échange est aussi rattaché à un cabinet,
   * donc visible sur sa fiche (cas homonyme, 69,6 % — GO Dove 03/09). Toujours
   * faux sur le fil d'un cabinet. Absent = faux.
   */
  aussiSurCabinet: z.boolean().default(false),
  /** LEQUEL (GO Dove 03/09) : fiche Twenty et nom du cabinet rattaché, quand la copie le connaît. */
  cabinet: z.object({ id: z.string(), nom: z.string() }).nullable().default(null),
  /** L2 (07/09) : le résumé IA de cet échange, quand la file l'a produit. */
  resume: EchangeResumeSchema.nullable().default(null),
});
export type Echange = z.infer<typeof EchangeSchema>;

/**
 * Ce que le rôle a le droit de voir est décidé au SERVEUR : quand un canal est
 * invisible, ses échanges ne sont ni calculés ni envoyés. Les drapeaux ne
 * servent qu'à écrire la bonne phrase à l'écran — « aucun échange » et « tu n'y
 * as pas accès » ne se disent pas pareil.
 */
export const EchangesSchema = z.object({
  items: z.array(EchangeSchema),
  emailsVisibles: z.boolean(),
  whatsappVisible: z.boolean(),
});
export type Echanges = z.infer<typeof EchangesSchema>;

export const ECHANGES_AFFICHES = 40;
export const LONGUEUR_APERCU = 120;

export const ECHANGES_VIDES: Echanges = { items: [], emailsVisibles: false, whatsappVisible: false };

/** Coupe proprement, sans laisser un mot à moitié. */
export function apercu(texte: string | null, limite = LONGUEUR_APERCU): string | null {
  const t = (texte ?? "").trim().replace(/\s+/g, " ");
  if (!t) return null;
  if (t.length <= limite) return t;
  const coupe = t.slice(0, limite);
  const espace = coupe.lastIndexOf(" ");
  return `${espace > limite * 0.6 ? coupe.slice(0, espace) : coupe}…`;
}

/** Le fil se lit du plus récent au plus ancien, tous canaux mêlés. */
export function trierEchanges(items: readonly Echange[]): Echange[] {
  return [...items].sort((a, b) => b.quand.localeCompare(a.quand)).slice(0, ECHANGES_AFFICHES);
}

export const CANAL_LIBELLE: Record<Canal, string> = { email: "E-mail", whatsapp: "WhatsApp" };
